import { APIFieldType, type ArdoqId } from '@ardoq/api-types';
import type { EnhancedScopeData } from '@ardoq/data-model';
import type { PermissionContext } from '@ardoq/access-control';
import { componentUtils } from '@ardoq/scope-data';
import { uniq, uniqBy } from 'lodash';
import {
  enhanceAttributesWithPermissions,
  type FieldWithPermissions,
} from 'streams/fields/getFieldsForViewpoint';
import {
  bubbleComponentFieldsWithScopeData,
  referenceFieldsWithScopeData,
  referencedComponentFieldsWithScopeData,
} from './viewModel$';
import type { ReferenceWithBubbleComponent } from './types';

export type ReferenceFields = {
  availableBubbleComponentFields: FieldWithPermissions[];
  availableReferenceFields: FieldWithPermissions[];
  availableReferencedComponentFields: FieldWithPermissions[];
  numericFields: FieldWithPermissions[];
};

const toBubbleComponentId = ({
  bubbleComponentId,
}: ReferenceWithBubbleComponent): ArdoqId => bubbleComponentId;

const toComponentId = ({
  componentId,
}: ReferenceWithBubbleComponent): ArdoqId => componentId;

type ComputeReferenceNumericFieldsArgs = {
  references: ReferenceWithBubbleComponent[];
  permissionContext: PermissionContext;
  scopeData: EnhancedScopeData;
};

/**
 * Computes numeric fields from the provided references.
 * In viewpoint mode, this receives references filtered by both direction
 * (incoming/outgoing) and reference type to ensure available fields
 * match the data that will be rendered.
 */
export const computeReferenceNumericFields = ({
  references,
  permissionContext,
  scopeData,
}: ComputeReferenceNumericFieldsArgs): ReferenceFields => {
  const uniqueComponentIds = uniq(
    references.flatMap(({ componentId, bubbleComponentId }) => [
      componentId,
      bubbleComponentId,
    ])
  );
  const componentsWorkspaceMap = new Map<ArdoqId, ArdoqId | null>(
    uniqueComponentIds.map(componentId => [
      componentId,
      componentUtils.getWorkspaceId(scopeData, componentId),
    ])
  );

  const [
    availableBubbleComponentFields,
    availableReferenceFields,
    availableReferencedComponentFields,
  ] = [
    {
      getFields: bubbleComponentFieldsWithScopeData,
      getComponentId: toBubbleComponentId,
    },
    {
      getFields: referenceFieldsWithScopeData,
      getComponentId: toComponentId,
    },
    {
      getFields: referencedComponentFieldsWithScopeData,
      getComponentId: toComponentId,
    },
  ].map(({ getFields, getComponentId }) =>
    uniqBy(
      references
        .flatMap(reference => {
          const fieldAttributes = getFields(scopeData, reference);
          const workspaceId = componentsWorkspaceMap.get(
            getComponentId(reference)
          );

          return fieldAttributes.map(attributes =>
            enhanceAttributesWithPermissions(
              permissionContext,
              attributes,
              workspaceId
            )
          );
        })
        .filter(({ type }) => type === APIFieldType.NUMBER),
      ({ name }) => name
    )
  );

  return {
    availableBubbleComponentFields,
    availableReferenceFields,
    availableReferencedComponentFields,
    numericFields: uniqBy(
      [
        ...availableReferenceFields,
        ...availableReferencedComponentFields,
        ...availableBubbleComponentFields,
      ],
      'name'
    ),
  };
};
